import React, { useContext, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import NavigationBar from '@/components/NavigationBar';
import { AuthContext } from './AuthContext';
import usePremiumEntitlement from './hooks/usePremiumEntitlement';
import { normalizeBillingPlans } from './billingPresentation';

const humanBetaCoreFeatures = [
  'All eight Stock Focus profiles',
  'Six researched Trading Strategies',
  'Pinngro Practice and Today',
  'Bot creation and periodic market checks',
  'Advanced and custom strategy tools',
];

const testPremiumFeatures = [
  'Historical Backtesting with reproducible results',
  'Saved Backtest jobs and result history',
  'Cancellation, recovery, verification, and exact replay',
  'Congressional & SEC Insider Disclosure Alerts with Today cards, bell alerts, and Daily Briefing updates',
];

const plannedMonthlyPrice = (plan) => (plan.monthly_target_cents === 0
  ? '$0'
  : `$${(plan.monthly_target_cents / 100).toFixed(2)} / month`);

const Subscription = () => {
  const {
    token, isTokenVerified, isAuthInitialized, authServiceUnavailable, axiosInstance,
  } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const authenticated = Boolean(token && isTokenVerified);
  const billingQuery = usePremiumEntitlement(authenticated);
  const publicPlansQuery = useQuery({
    queryKey: ['billing-plans'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/billing/plans');
      return normalizeBillingPlans(response.data);
    },
    enabled: isAuthInitialized && !token,
    retry: 1,
    staleTime: 30000,
  });
  const [workingPlan, setWorkingPlan] = useState(null);
  const [actionError, setActionError] = useState(null);
  const actionInFlight = useRef(false);

  const waitingForAuthentication = !isAuthInitialized
    || Boolean(token && !isTokenVerified && !authServiceUnavailable);
  const authenticationUnavailable = Boolean(
    token && !isTokenVerified && authServiceUnavailable,
  );
  const activeQuery = token ? billingQuery : publicPlansQuery;
  const billing = activeQuery.data || null;
  const plannedCatalog = billing?.planned_catalog || null;
  const loading = waitingForAuthentication
    || (!authenticationUnavailable && activeQuery.isPending);
  const loadFailed = Boolean(
    authenticationUnavailable
    || (!loading && activeQuery.isError && !billing),
  );

  const openCheckout = async () => {
    if (actionInFlight.current) return;
    if (!token) {
      window.location.assign('/login');
      return;
    }
    actionInFlight.current = true;
    setWorkingPlan('premium');
    setActionError(null);
    try {
      const response = await axiosInstance.post('/api/billing/checkout', { plan: 'premium' });
      window.location.assign(response.data.checkout_url);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setActionError({ action: 'premium', message: typeof detail === 'string' ? detail : 'Unable to start Test checkout. Please try again.' });
      setWorkingPlan(null);
      actionInFlight.current = false;
    }
  };

  const openPortal = async () => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setWorkingPlan('portal');
    setActionError(null);
    try {
      const response = await axiosInstance.post('/api/billing/portal');
      window.location.assign(response.data.portal_url);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setActionError({ action: 'portal', message: typeof detail === 'string' ? detail : 'Unable to open billing management. Please try again.' });
      setWorkingPlan(null);
      actionInFlight.current = false;
    }
  };

  const checkoutResult = searchParams.get('checkout');
  const currentPlan = billing?.plan || 'free';
  const hasPaidTestPlan = Boolean(token && currentPlan !== 'free');
  const premiumTestPrice = billing?.plans?.premium?.amount_cents ?? 2500;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      <main className="mx-auto max-w-6xl px-4 pb-20 pt-28 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Pinngro plans</p>
          <h1 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Today&apos;s Beta access and tomorrow&apos;s plans</h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
            Human Beta access is separate from Pinngro&apos;s planned post-Beta packaging. Planned tiers below are a roadmap preview, not an offer to purchase.
          </p>
        </div>

        <section className="mt-10 rounded-3xl border border-blue-200 bg-white p-5 shadow-sm dark:border-blue-900 dark:bg-slate-900 sm:p-8" aria-labelledby="human-beta-today-heading">
          <div className="flex flex-col gap-3 border-b border-slate-200 pb-6 dark:border-slate-800 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-700 dark:text-blue-300">Current Human Beta</p>
              <h2 id="human-beta-today-heading" className="mt-2 text-3xl font-black">Human Beta today</h2>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                Current Human Beta access is not final launch packaging. Core Pinngro functionality remains available under the current Beta entitlement contract; Premium adds Backtesting and SEC insider disclosure alerts.
              </p>
            </div>
            <span className="w-fit rounded-full bg-blue-100 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-blue-800 dark:bg-blue-950 dark:text-blue-200">Active now</span>
          </div>

          <div className="mt-6 rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 text-amber-950" role="note">
            <p className="font-black">Human Beta — Stripe Test Mode — no real charge</p>
            <p className="mt-2 text-sm leading-6">Never enter a real card. Use only Stripe&apos;s official test-card details supplied in the Beta instructions. Current Test fixture pricing is not final launch pricing.</p>
          </div>

          {checkoutResult === 'success' && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
              Test checkout completed. Stripe is confirming the Test subscription; refresh shortly if the entitlement has not updated yet.
            </div>
          )}
          {checkoutResult === 'canceled' && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              Test checkout was canceled. No plan change was made.
            </div>
          )}
          {loading && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300" role="status">
              Loading billing details…
            </div>
          )}

          {loadFailed && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100" role="alert">
              <p className="font-bold">Billing details are temporarily unavailable.</p>
              <p className="mt-2 text-sm">Your account and subscription have not been changed. Please try again.</p>
              {!authenticationUnavailable && activeQuery.refetch && (
                <button type="button" onClick={() => activeQuery.refetch()} disabled={activeQuery.isFetching} className="mt-4 rounded-xl bg-amber-900 px-4 py-2 font-bold text-white disabled:opacity-60 dark:bg-amber-200 dark:text-amber-950">
                  {activeQuery.isFetching ? 'Trying again…' : 'Try again'}
                </button>
              )}
            </div>
          )}

          {billing && token && currentPlan === 'basic' && !loading && (
            <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5 text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100" aria-labelledby="basic-compatibility-heading">
              <h3 id="basic-compatibility-heading" className="font-black">Existing Basic Test subscription</h3>
              <p className="mt-2 text-sm leading-6">Basic remains a supported Human Beta billing fixture, but it adds no product entitlement beyond Free. It is not a planned post-Beta customer tier and does not unlock Premium Backtesting, more bots, or faster market checks.</p>
            </div>
          )}

          {billing && !loading && (
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              <article className={`rounded-2xl border p-6 ${currentPlan === 'free' ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-100 dark:bg-blue-950/20 dark:ring-blue-950' : 'border-slate-200 dark:border-slate-700'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700 dark:text-blue-300">Current Beta core access</p>
                    <h3 className="mt-2 text-2xl font-black">Free</h3>
                  </div>
                  {currentPlan === 'free' && <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase text-blue-800">Current plan</span>}
                </div>
                <p className="mt-4 text-4xl font-black">$0</p>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Core Pinngro access with no subscription required.</p>
                <ul className="mt-5 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                  {humanBetaCoreFeatures.map((feature) => <li key={feature}>✓ {feature}</li>)}
                </ul>
                <p className="mt-6 rounded-xl bg-white px-4 py-3 text-center text-sm font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {currentPlan === 'free' ? 'Free — Current plan' : 'Core access remains included'}
                </p>
              </article>

              <article className="rounded-2xl border border-violet-200 bg-violet-50/50 p-6 dark:border-violet-900 dark:bg-violet-950/20" aria-labelledby="test-premium-heading">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-violet-700 dark:text-violet-300">Test billing fixture</p>
                    <h3 id="test-premium-heading" className="mt-2 text-2xl font-black">Test Premium Backtesting checkout</h3>
                  </div>
                  {currentPlan === 'premium' && <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase text-violet-800">Test entitlement active</span>}
                </div>
                <p className="mt-4 text-4xl font-black">${(premiumTestPrice / 100).toFixed(0)}<span className="text-base font-semibold text-slate-500"> Test fixture / month</span></p>
                <p className="mt-2 text-sm font-bold text-violet-800 dark:text-violet-200">Test fixture price — not planned launch pricing</p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">This optional flow exercises subscription and billing behavior during Human Beta. Stripe Test Mode only; no real charge.</p>
                <ul className="mt-5 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                  {testPremiumFeatures.map((feature) => <li key={feature}>✓ {feature}</li>)}
                </ul>
                {currentPlan === 'premium' || hasPaidTestPlan ? (
                  <p className="mt-6 rounded-xl bg-white px-4 py-3 text-center text-sm font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {currentPlan === 'premium' ? 'Premium Test entitlement — Current' : 'Manage your existing Test subscription below'}
                  </p>
                ) : (
                  <button
                    type="button"
                    disabled={Boolean(workingPlan) || (token && !billing.payments_enabled)}
                    onClick={openCheckout}
                    className="mt-6 w-full rounded-xl bg-violet-700 px-4 py-3 font-bold text-white transition hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {workingPlan === 'premium' ? 'Opening Test checkout…' : token ? 'Open Test Premium Checkout' : 'Sign in to use Test checkout'}
                  </button>
                )}
                {workingPlan === 'premium' && <p role="status" className="mt-2 text-sm text-violet-900 dark:text-violet-200">Preparing your Test checkout…</p>}
                {actionError?.action === 'premium' && <p role="alert" className="mt-2 rounded-xl bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{actionError.message}</p>}
              </article>
            </div>
          )}

          {billing && token && currentPlan !== 'free' && (
            <div className="mt-6 text-center">
              <button type="button" onClick={openPortal} disabled={Boolean(workingPlan)} className="font-bold text-blue-700 hover:underline disabled:text-slate-400">
                {workingPlan === 'portal' ? 'Opening billing management…' : 'Manage billing, invoices, or cancellation (Test Mode)'}
              </button>
              {workingPlan === 'portal' && <p role="status" className="mt-2 text-sm text-blue-700 dark:text-blue-300">Opening billing management…</p>}
              {actionError?.action === 'portal' && <p role="alert" className="mt-2 rounded-xl bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{actionError.message}</p>}
            </div>
          )}

          {billing && token && billing.payments_enabled === false && !loading && (
            <p className="mt-6 text-center text-sm text-slate-500">
              Secure Test checkout is being configured. The roadmap preview remains visible, but Test payments are not enabled yet.
            </p>
          )}
        </section>

        <section className="mt-16 border-t-4 border-slate-300 pt-12 dark:border-slate-700" aria-labelledby="planned-plans-heading">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Planned after Human Beta</p>
            <h2 id="planned-plans-heading" className="mt-3 text-3xl font-black sm:text-4xl">Planned launch plans</h2>
            <p className="mt-4 text-base font-bold text-slate-700 dark:text-slate-200">Planned post-Beta pricing · Not yet available for purchase</p>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">These roadmap cards describe intended packaging. They do not change current access, enforce bot limits, or connect to Stripe Checkout.</p>
          </div>

          {!loading && billing && !plannedCatalog && (
            <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center text-amber-950" role="alert">
              The planned catalog is temporarily unavailable. Current Human Beta access is unaffected.
            </div>
          )}

          {plannedCatalog && (
            <div className="mt-9 grid gap-6 lg:grid-cols-3" data-planned-catalog={plannedCatalog.contract}>
              {plannedCatalog.plans.map((plan) => (
                <article key={plan.id} data-planned-tier={plan.id} className="flex flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-2xl font-black">{plan.name}</h3>
                      <p className="mt-1 text-sm font-bold text-emerald-700 dark:text-emerald-300">{plan.positioning}</p>
                    </div>
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">Planned</span>
                  </div>
                  <p className="mt-6 text-3xl font-black">{plannedMonthlyPrice(plan)}</p>
                  {plan.annual_target_cents > 0 && (
                    <p className="mt-1 text-sm font-bold text-slate-500">${(plan.annual_target_cents / 100).toFixed(0)} / year target</p>
                  )}
                  <p className="mt-4 text-sm font-bold text-slate-700 dark:text-slate-200">Planned capacity: {plan.planned_active_bot_limit} active {plan.planned_active_bot_limit === 1 ? 'bot' : 'bots'}</p>
                  <ul className="mt-5 flex-1 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                    {plan.planned_features.map((feature) => <li key={feature}>✓ {feature}</li>)}
                  </ul>
                  <p className="mt-6 rounded-xl bg-slate-100 px-4 py-3 text-center text-xs font-black uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">Not available for purchase yet</p>
                </article>
              ))}
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm leading-6 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100" role="note">
            <p className="font-black">Live trading is planned for Trader and Premium; it is not enabled in Human Beta.</p>
            <p className="mt-1">It remains subject to separate legal, security, implementation, and acceptance approval. Planned packaging cannot authorize Paper or Live broker execution.</p>
          </div>

          <p className="mt-6 text-center text-sm leading-6 text-slate-500 dark:text-slate-400">
            Planned subscriptions use SaaS pricing—not per-trade charges, a percentage of gains, or portfolio-value pricing. Customer assets remain with the broker; Today remains core, Backtesting remains Premium&apos;s principal differentiator, and initial bot packs are not planned.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Subscription;
